(function () {
  // The frozen ZIP-based helpers are retained here only long enough to remove
  // any old line items that may still be in a customer's cart.
  const LEGACY_FROZEN_HELPER_SKUS = new Set(['114579', '106150']);
  const ACTIVE_HELPER_SKUS = new Set(['122942', '117667']);
  const CART_COMMIT_DELAY_MS = 350;
  let cartUpdateTimer;
  let reconciliationVersion = 0;
  let reconciliationRunning = false;
  let reconciliationQueued = false;
  let deliveryAddonWatchScheduled = false;

  function isHelper(item) {
    const sku = String(item.sku || '').trim();
    return LEGACY_FROZEN_HELPER_SKUS.has(sku) || ACTIVE_HELPER_SKUS.has(sku) || item.properties?._af_helper === 'true';
  }

  async function getCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Could not read cart');
    return response.json();
  }

  async function changeLine(line, quantity) {
    const response = await fetch(window.routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({ line, quantity }),
    });
    if (!response.ok) throw new Error(`Could not update cart helper line (${response.status})`);
    return response;
  }

  async function addVariant(variantId) {
    const response = await fetch(window.routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: { _af_helper: 'true' },
      }),
    });
    if (!response.ok) throw new Error(`Could not add required cart helper (${response.status})`);
    return response;
  }

  function desiredHeatThermalVariant(config) {
    return config.hasHeatSensitiveItems ? config.heatThermalVariantId : '';
  }

  async function getRulesConfig(forceRemote = false) {
    const element = document.getElementById('AfCartRulesConfig');
    if (!forceRemote && element && element.dataset.config) {
      return JSON.parse(element.dataset.config);
    }

    // A cartUpdate event can be published just before Shopify's section render
    // reflects the new line state. Avoid a cached pre-removal configuration.
    const response = await fetch(`${window.routes.cart_url}?section_id=main-cart-items&_af_cart_rules=${Date.now()}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Could not read cart rules');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = html.getElementById('AfCartRulesConfig');
    return source && source.dataset.config ? JSON.parse(source.dataset.config) : null;
  }

  async function refreshCartSections(version = reconciliationVersion) {
    // Helper removals use Shopify's cart-change API directly, so Dawn's
    // CartItems.updateQuantity() does not get a chance to toggle the empty
    // state on the outer components. Read it here before replacing sections.
    const currentCart = await getCart();
    if (version !== reconciliationVersion) return false;
    const isEmpty = Number(currentCart.item_count || 0) === 0;
    document.querySelectorAll('cart-items, #main-cart-footer, cart-drawer').forEach((element) => {
      element.classList.toggle('is-empty', isEmpty);
    });

    // The cart page sections are dynamic template instances. Requesting their
    // static filenames renders them without the merchant-configured blocks;
    // for main-cart-footer that produces an empty footer and removes Checkout.
    // Keep the DOM nodes stable, but fetch their real section instance IDs.
    const cartItemsSectionId = document.getElementById('main-cart-items')?.dataset.id || 'main-cart-items';
    const cartFooterSectionId = document.getElementById('main-cart-footer')?.dataset.id || 'main-cart-footer';
    const requestedSections = [cartItemsSectionId, cartFooterSectionId, 'cart-drawer', 'cart-icon-bubble'];
    const sectionParams = new URLSearchParams({
      sections: requestedSections.join(','),
      sections_url: window.location.pathname,
      _af_cart_sections: String(Date.now()),
    });
    const response = await fetch(`${window.routes.cart_url}?${sectionParams.toString()}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) return;
    const sections = await response.json();
    if (version !== reconciliationVersion) return false;
    const replaceInner = (sectionId, targetId, selector) => {
      if (!sections[sectionId]) return;
      const source = new DOMParser().parseFromString(sections[sectionId], 'text/html').querySelector(selector);
      const target = document.querySelector(targetId);
      if (source && target) target.innerHTML = source.innerHTML;
    };

    replaceInner(cartItemsSectionId, 'cart-items', 'cart-items');
    replaceInner(cartFooterSectionId, '#main-cart-footer', '#main-cart-footer');
    replaceInner('cart-drawer', '.drawer__inner', '.drawer__inner');
    replaceInner('cart-icon-bubble', '#cart-icon-bubble', '.shopify-section');
    return true;
  }

  async function syncCartHelpers(options = {}) {
    const config = await getRulesConfig(Boolean(options.forceRemote));
    if (!config || sessionStorage.getItem('afCartRulesSyncing') === 'true') return;

    sessionStorage.setItem('afCartRulesSyncing', 'true');
    try {
      let changed = false;
      // Re-read the cart after every mutation: line positions change after a removal.
      while (true) {
        const cart = await getCart();
        const desiredHeatThermal = desiredHeatThermalVariant(config);
        const desiredSignature = config.hasAlcoholItems ? config.signatureVariantId : '';
        const items = cart.items || [];
        const legacyLine = items.findIndex((item) => LEGACY_FROZEN_HELPER_SKUS.has(String(item.sku || '').trim()));
        const invalidThermalLine = items.findIndex((item) => String(item.sku || '').trim() === '122942' && String(item.variant_id) !== String(desiredHeatThermal));
        const invalidSignatureLine = items.findIndex((item) => String(item.sku || '').trim() === '117667' && !desiredSignature);
        const desiredThermalLines = desiredHeatThermal
          ? items.reduce((lines, item, index) => {
              if (String(item.variant_id) === String(desiredHeatThermal)) lines.push(index);
              return lines;
            }, [])
          : [];
        const desiredSignatureLines = desiredSignature
          ? items.reduce((lines, item, index) => {
              if (String(item.variant_id) === String(desiredSignature)) lines.push(index);
              return lines;
            }, [])
          : [];
        const duplicateThermalLine = desiredThermalLines.length > 1 ? desiredThermalLines[desiredThermalLines.length - 1] : -1;
        const duplicateSignatureLine = desiredSignatureLines.length > 1 ? desiredSignatureLines[desiredSignatureLines.length - 1] : -1;
        const thermalQuantityLine = desiredThermalLines.find((index) => Number(items[index].quantity) !== 1) ?? -1;
        const signatureQuantityLine = desiredSignatureLines.find((index) => Number(items[index].quantity) !== 1) ?? -1;
        const hasDesiredThermal = desiredThermalLines.length > 0;
        const hasDesiredSignature = desiredSignatureLines.length > 0;

        if (legacyLine !== -1) {
          await changeLine(legacyLine + 1, 0);
        } else if (invalidThermalLine !== -1) {
          await changeLine(invalidThermalLine + 1, 0);
        } else if (invalidSignatureLine !== -1) {
          await changeLine(invalidSignatureLine + 1, 0);
        } else if (duplicateThermalLine !== -1) {
          await changeLine(duplicateThermalLine + 1, 0);
        } else if (duplicateSignatureLine !== -1) {
          await changeLine(duplicateSignatureLine + 1, 0);
        } else if (thermalQuantityLine !== -1) {
          await changeLine(thermalQuantityLine + 1, 1);
        } else if (signatureQuantityLine !== -1) {
          await changeLine(signatureQuantityLine + 1, 1);
        } else if (desiredHeatThermal && !hasDesiredThermal) {
          await addVariant(desiredHeatThermal);
        } else if (desiredSignature && !hasDesiredSignature) {
          await addVariant(desiredSignature);
        } else {
          break;
        }
        changed = true;
      }
      if (changed || options.refresh) await refreshCartSections(options.version);
    } finally {
      sessionStorage.removeItem('afCartRulesSyncing');
    }
  }

  window.afCartRules = { syncCartHelpers };

  function waitForCartCommit() {
    return new Promise((resolve) => window.setTimeout(resolve, CART_COMMIT_DELAY_MS));
  }

  async function reconcileAfterCartUpdate(version) {
    if (reconciliationRunning) {
      reconciliationQueued = true;
      return;
    }

    reconciliationRunning = true;
    try {
      // First pass handles the usual AJAX update. The confirmation pass runs
      // after Shopify has committed the removed line and clears any helper
      // that the first pass saw as temporarily still required.
      await syncCartHelpers({ refresh: true, forceRemote: true, version });
      await waitForCartCommit();
      await syncCartHelpers({ refresh: true, forceRemote: true, version });
    } catch (error) {
      console.warn(error);
    } finally {
      // Always repaint the footer from the committed cart. This is separate
      // from helper reconciliation because pre-order removal has no helper
      // mutation to trigger a redraw.
      try {
        await refreshCartSections(version);
        document.dispatchEvent(new Event('af-cart-rules-sections-refreshed'));
      } catch (error) {
        console.warn(error);
      }
      reconciliationRunning = false;
      if (reconciliationQueued) {
        reconciliationQueued = false;
        reconcileAfterCartUpdate(reconciliationVersion);
      }
    }
  }

  function scheduleCartReconciliation() {
    const version = ++reconciliationVersion;
    window.clearTimeout(cartUpdateTimer);
    cartUpdateTimer = window.setTimeout(() => reconcileAfterCartUpdate(version), 0);
  }

  function bindDeliveryAddonWatcher() {
    const cartItemsRoot = document.getElementById('main-cart-items') || document.querySelector('cart-items');
    if (!cartItemsRoot || cartItemsRoot.dataset.afDeliveryAddonWatcher === 'true') return;

    const observer = new MutationObserver(() => {
      if (deliveryAddonWatchScheduled) return;
      deliveryAddonWatchScheduled = true;
      window.setTimeout(() => {
        deliveryAddonWatchScheduled = false;
        scheduleCartReconciliation();
      }, 150);
    });

    observer.observe(cartItemsRoot, { childList: true, subtree: true });
    cartItemsRoot.dataset.afDeliveryAddonWatcher = 'true';
  }

  document.addEventListener('DOMContentLoaded', () => {
    syncCartHelpers().catch((error) => console.warn(error));
    bindDeliveryAddonWatcher();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      // The drawer and full-cart sections do not always refresh together.
      // Re-read the committed server state, then confirm it once more after
      // Shopify's line-removal render has settled.
      scheduleCartReconciliation();
    });
  }

  if (!window.afCartRulesThemeEventsBound) {
    const rebindWatcher = () => bindDeliveryAddonWatcher();
    document.addEventListener('shopify:section:load', rebindWatcher);
    document.addEventListener('shopify:section:reorder', rebindWatcher);
    window.afCartRulesThemeEventsBound = true;
  }
})();
